import { createTheme, alpha, darken } from "@mui/material/styles";

// Brand palette pulled from the Salatty logo. Greens are the basket gradient
// and app-icon gradient; amber is the handle/spark accent; the darkest greens
// are the wordmark/dark-variant anchors, reused here for the sidebar and
// deep-tone semantic colors so everything stays within the brand set.
// Mirrors ../BusinessOwner/theme.js so the delivery-company console shares
// the same UI/UX as the business-owner console.
const brand = {
  emeraldLight: "#16C79A",
  emeraldDeep: "#067A5B",
  tealStart: "#12B886",
  tealEnd: "#056150",
  amber: "#FFB627",
  amberLight: "#FFC93C",
  forestGreen: "#0A6B52",
  inkGreen: "#113B36",
  nightGreen: "#0B2B26",
};

const deliveryCompanyTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: brand.tealStart,
      light: brand.emeraldLight,
      dark: brand.emeraldDeep,
      contrastText: "#ffffff",
    },
    secondary: {
      main: brand.amber,
      light: brand.amberLight,
      dark: darken(brand.amber, 0.25),
      contrastText: brand.inkGreen,
    },
    success: {
      main: brand.forestGreen,
      light: brand.tealStart,
      dark: brand.emeraldDeep,
    },
    warning: {
      main: brand.amber,
      light: brand.amberLight,
      dark: darken(brand.amber, 0.2),
    },
    info: {
      main: brand.tealEnd,
      light: brand.forestGreen,
      dark: brand.nightGreen,
    },
    error: { main: "#dc2626" },
    background: {
      default: "#F3FBF8",
      paper: "#ffffff",
    },
    text: {
      primary: brand.inkGreen,
      secondary: "#4B6E67",
    },
    divider: "#DCEFE8",
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      "Helvetica",
      "Arial",
      "sans-serif",
    ].join(","),
    h4: { fontWeight: 800, letterSpacing: "-0.02em" },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          scrollbarWidth: "thin",
          scrollbarColor: `${alpha(brand.emeraldDeep, 0.35)} transparent`,
        },
        "*::-webkit-scrollbar": { width: 6, height: 6 },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: alpha(brand.emeraldDeep, 0.35),
          borderRadius: 8,
        },
        "*::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 2px rgba(11, 43, 38, 0.06)",
          borderBottom: `1px solid ${brand.tealStart}1A`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(11, 43, 38, 0.08)",
          border: `1px solid ${alpha(brand.tealStart, 0.14)}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          boxShadow: "0 12px 32px rgba(11, 43, 38, 0.18)",
          border: `1px solid ${alpha(brand.tealStart, 0.14)}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: brand.inkGreen,
          fontSize: 12,
          fontWeight: 500,
          padding: "6px 10px",
          borderRadius: 6,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 700,
          borderRadius: 10,
          paddingInline: 16,
          transition: "transform 0.15s ease, box-shadow 0.15s ease, background-image 0.15s ease",
        },
        containedPrimary: {
          backgroundImage: `linear-gradient(135deg, ${brand.emeraldLight}, ${brand.emeraldDeep})`,
          boxShadow: `0 6px 16px ${alpha(brand.emeraldDeep, 0.35)}`,
          "&:hover": {
            backgroundImage: `linear-gradient(135deg, ${brand.tealStart}, ${brand.emeraldDeep})`,
            boxShadow: `0 8px 20px ${alpha(brand.emeraldDeep, 0.42)}`,
            transform: "translateY(-1px)",
          },
        },
        containedSecondary: {
          backgroundImage: `linear-gradient(135deg, ${brand.amberLight}, ${brand.amber})`,
          color: brand.inkGreen,
          boxShadow: `0 6px 16px ${alpha(brand.amber, 0.4)}`,
          "&:hover": {
            backgroundImage: `linear-gradient(135deg, ${brand.amberLight}, ${darken(brand.amber, 0.15)})`,
            boxShadow: `0 8px 20px ${alpha(brand.amber, 0.48)}`,
            transform: "translateY(-1px)",
          },
        },
        outlinedPrimary: {
          borderColor: alpha(brand.tealStart, 0.4),
          "&:hover": {
            borderColor: brand.tealStart,
            backgroundColor: alpha(brand.tealStart, 0.08),
          },
        },
        outlined: {
          borderWidth: 1.5,
          "&:hover": { borderWidth: 1.5 },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 999,
          padding: 4,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          "& .MuiToggleButtonGroup-grouped": {
            margin: "0 2px",
            border: "none",
            "&:first-of-type": { borderRadius: 999 },
            "&:not(:first-of-type)": { borderRadius: 999 },
            "&:last-of-type": { borderRadius: 999 },
          },
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "none",
          fontWeight: 700,
          lineHeight: 1,
          height: 34,
          boxSizing: "border-box",
          padding: "0 16px",
          border: "none",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          whiteSpace: "nowrap",
          color: theme.palette.text.secondary,
          transition: "background-image 0.15s ease, color 0.15s ease, box-shadow 0.15s ease",
          "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
          "&.Mui-selected": {
            color: "#fff",
            backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
            "&:hover": {
              backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
            },
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 42,
          padding: 4,
          borderRadius: 999,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          "& .MuiTabs-flexContainer": { gap: 4, alignItems: "center" },
        }),
        indicator: { display: "none" },
        scroller: { display: "flex", alignItems: "center" },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "none",
          fontWeight: 700,
          lineHeight: 1,
          height: 34,
          minHeight: 34,
          minWidth: "auto",
          boxSizing: "border-box",
          borderRadius: 999,
          padding: "0 16px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          whiteSpace: "nowrap",
          color: theme.palette.text.secondary,
          transition: "background-image 0.15s ease, color 0.15s ease, box-shadow 0.15s ease",
          "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
          "&.Mui-selected": {
            color: "#fff",
            backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          "--DataGrid-rowBorderColor": "#EAF6F1",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#EFF9F5",
            borderBottom: `1px solid ${brand.tealStart}33`,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "#4B6E67",
          },
          "& .MuiDataGrid-columnSeparator": { display: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "1px solid #EAF6F1" },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
          "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "#F3FBF8" },
          "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${brand.tealStart}33` },
          "& .MuiDataGrid-overlay": { backgroundColor: "rgba(255,255,255,0.6)" },
        },
      },
    },
  },
});

export default deliveryCompanyTheme;
export { brand };
